import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { 
  Upload, X, Crop, Check, RotateCcw, ZoomIn, ZoomOut, 
  Image as ImageIcon, Link, Loader2, Move
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Slider } from './slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Aspect ratio presets for crop
const aspectRatioPresets = [
  { value: 'free', label: 'Free Crop', ratio: undefined },
  { value: '1:1', label: 'Square (1:1)', ratio: 1 },
  { value: '16:9', label: 'Wide (16:9)', ratio: 16/9 },
  { value: '9:16', label: 'Portrait (9:16)', ratio: 9/16 },
  { value: '4:3', label: 'Standard (4:3)', ratio: 4/3 },
  { value: '3:2', label: 'Photo (3:2)', ratio: 3/2 },
];

export const ImageUploader = ({ 
  value, 
  onChange, 
  label = "Image",
  aspectRatio = undefined, // undefined means free crop by default
  allowFreeCrop = true,
  className = "",
  token
}) => {
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('free');
  const fileInputRef = useRef(null);

  // Get the current aspect ratio value
  const getCurrentAspect = () => {
    // If a specific preset is selected
    const preset = aspectRatioPresets.find(ar => ar.value === selectedAspectRatio);
    if (preset) {
      return preset.ratio; // undefined for free crop, number for fixed
    }
    // Fallback to free crop
    return undefined;
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop({
        file,
        preview: reader.result
      });
      setCropModalOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setSelectedAspectRatio('free'); // Always start with free crop
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };

  const uploadCroppedImage = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageToCrop.file);
      
      // Calculate crop percentages
      const img = new window.Image();
      img.src = imageToCrop.preview;
      await new Promise(resolve => img.onload = resolve);
      
      const cropX = (croppedAreaPixels.x / img.width) * 100;
      const cropY = (croppedAreaPixels.y / img.height) * 100;
      const cropWidth = (croppedAreaPixels.width / img.width) * 100;
      const cropHeight = (croppedAreaPixels.height / img.height) * 100;
      
      formData.append('crop_x', cropX.toString());
      formData.append('crop_y', cropY.toString());
      formData.append('crop_width', cropWidth.toString());
      formData.append('crop_height', cropHeight.toString());

      const response = await fetch(`${API}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      onChange(data.url);
      setCropModalOpen(false);
      setImageToCrop(null);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    // Basic URL validation
    try {
      new URL(urlInput);
      onChange(urlInput);
      setUrlInput('');
      toast.success('Image URL added');
    } catch {
      toast.error('Please enter a valid URL');
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>{label}</Label>
      
      {/* Preview */}
      {value && (
        <div className="relative group">
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-40 object-cover rounded-lg border border-slate-200"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" font-family="sans-serif" font-size="12" fill="%2394a3b8" text-anchor="middle" dy=".3em">Image Error</text></svg>';
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleRemove}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Upload Options */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload" className="text-xs">
            <Upload className="w-3 h-3 mr-1" /> Upload
          </TabsTrigger>
          <TabsTrigger value="url" className="text-xs">
            <Link className="w-3 h-3 mr-1" /> URL
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="mt-2">
          <div 
            className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Click to upload image</p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP up to 10MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </TabsContent>
        
        <TabsContent value="url" className="mt-2">
          <div className="flex gap-2">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1"
            />
            <Button type="button" onClick={handleUrlSubmit} variant="outline">
              <Check className="w-4 h-4" />
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Crop Modal */}
      <Dialog open={cropModalOpen} onOpenChange={(open) => !uploading && setCropModalOpen(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="w-5 h-5" /> Crop Image
            </DialogTitle>
          </DialogHeader>

          {/* Aspect Ratio Selector */}
          {allowFreeCrop && (
            <div className="space-y-2">
              <Label className="text-sm text-slate-600 flex items-center gap-2">
                <Move className="w-4 h-4" /> Select Crop Mode:
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                {aspectRatioPresets.map((ar) => (
                  <button
                    key={ar.value}
                    type="button"
                    onClick={() => {
                      setSelectedAspectRatio(ar.value);
                      // Reset crop position when changing aspect
                      setCrop({ x: 0, y: 0 });
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      selectedAspectRatio === ar.value
                        ? 'bg-sky-500 text-white shadow-md'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                    }`}
                  >
                    {ar.label}
                  </button>
                ))}
              </div>
              {selectedAspectRatio === 'free' && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  ✓ Free crop enabled - drag any corner to resize freely
                </p>
              )}
            </div>
          )}
          
          <div className="relative h-96 bg-slate-900 rounded-lg overflow-hidden">
            {imageToCrop && (
              <Cropper
                image={imageToCrop.preview}
                crop={crop}
                zoom={zoom}
                aspect={getCurrentAspect()}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={true}
                cropShape="rect"
              />
            )}
          </div>

          {/* Zoom Control */}
          <div className="flex items-center gap-4 px-4">
            <ZoomOut className="w-4 h-4 text-slate-500" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={([value]) => setZoom(value)}
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-slate-500" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCropModalOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button 
              onClick={uploadCroppedImage}
              disabled={uploading}
              className="bg-sky-500 hover:bg-sky-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Crop & Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploader;
